(ns kyber.utils
  (:require [cljs-time.core :as t]
            [cljs-time.format :as f]
            [cljs-time.coerce :as coerce]
            [goog.color :as c]
            [goog.color.alpha :as ca]
            [goog.object :as gobj]
            [goog.string :as gstring]
            [goog.string.format]
            [clojure.string :as str]

            [cemerick.url :refer [url map->query]]
            [applied-science.js-interop :as j]
            [re-frame.core :as re-frame]

            [kyber.i18n.core :as i18n])
  (:import [goog.async Debouncer]))

(defn all-resources-loaded?
  "Returns true if the resource-status map provided by the router-ctx
  has been completely loaded"
  [resource-status]
  (let [loading-status (-> resource-status vals set)]
    (and (= 1 (count loading-status))
         (= :loaded (first loading-status)))))

(defn load-js
  "Dynamically load js files"
  ([src]
   (load-js src nil))
  ([src on-load]
   (let [script (.createElement js/document "script")]
     (gobj/set script "src" src)

     (when on-load
       (gobj/set script "onload" on-load))

     (-> js/document .-head (.appendChild script)))))

(defn gen-cid
  "Generates a context id used for analytics"
  []
  (keyword (str "_" (random-uuid))))

(defn debounce
  ^{:attribution (str "https://www.martinklepsch.org/"
                      "posts/simple-debouncing-in-clojurescript.html")}
  [f interval]
  (let [dbnc (Debouncer. f interval)]
    ;; We use apply here to support functions of various arities
    (fn [& args] (.apply (.-fire dbnc) dbnc (to-array args)))))

(defn get-ordinal [n]
  ^{:attribution (str "https://community.shopify.com/c/Shopify-Design/
                       Ordinal-Number-in-javascript-1st-2nd-3rd-4th/m-p/72156")}
  (let [s ["th" "st" "nd" "rd"]
        v (rem n 100)]
    (str n (or (nth s (rem (- v 20) 10) nil)
               (nth s v nil)
               (first s)))))

(defn pluralize [label number]
  (if (= 1 number) label (str label "s")))

(defn element?
  [e]
  (instance? js/HTMLElement e))

(defn drop-at-idx
  "removes the n'th index of a vector"
  [idx coll]
  (if (or (neg? idx)
          (> idx (count coll)))
    coll
    (into (subvec coll 0 idx)
          (subvec coll (inc idx)))))

(defn index-of [s v]
  (loop [idx 0 items s]
    (cond
      (empty? items) nil
      (= v (first items)) idx
      :else (recur (inc idx) (rest items)))))

(defn seconds->number-of-days-string
  [seconds]
  (let [number-of-days (/ seconds (* 60 60 24))]
    (if (= 1 number-of-days)
      "1 day"
      (str number-of-days " days"))))

(def date->day-string
  (let [unparse (partial f/unparse (f/formatter "MM/DD/YYYY"))]
    #(-> % (js/Date.) t/to-default-time-zone unparse)))

(def date->date-time-string
  ;; "MM/DD/YYYY h:mm a"
  ;; TODO 2022-06-10 Justin pull out t* and recactor callers
  ;; Ideally the callers of this should be refactored to pass a date into a direct call to t*
  ;; Subscriptions don't belong in utility functions
  #(->> % (js/Date.) (i18n/t* ::i18n-date->date-time-string)))

(defn seconds->time-string
  "Converts seconds to 00:00:00.0 format"
  ([seconds]
   (seconds->time-string seconds false))
  ([seconds show-sub-second]
   (let [fmt-seconds (.floor js/Math seconds)
         mills (.floor js/Math (* 10 (mod seconds 1)))
         now (t/now)
         date (t/date-time
               (t/year now)
               (t/month now)
               (t/day now)
               0
               0
               fmt-seconds
               mills)]
     (f/unparse
      (f/formatter (if show-sub-second
                     "HH:mm:ss.S"
                     "HH:mm:ss"))
      date))))

(defn fmt-time-string
  "Converts time in 00:xx:xx to xx:xx"
  [time-str]
  ;; It seems like this might only take output from
  ;; :kyber.utils/i18n-seconds->time-string but not verified
  ;;
  ;; formerly :kyber.utils/i18n-fmt-time-string but it was too difficult and ?unecessary? to localise given translation standards
  ;; It takes a string, not an inst
  ;; If localisation is necessary then larger refactoring is required
  (if (= (take 2 time-str) '("0" "0"))
    (str/join (take-last 7 (str/split time-str #"")))
    time-str))

(defn ->display-time
  ([time]
   (->display-time time false))
  ([time show-sub-second]
   (-> time
       (seconds->time-string show-sub-second)
       (fmt-time-string))))

(defn prevent-bubbling
  "Prevents an event from bubbling"
  [event-fn]
  (fn [e]
    (.stopPropagation e)
    (event-fn e)))

(defn split-color
  "Split a theme color into its MUI shades"
  [hex-color percent-opacity bg-color]
  (when (= (c/isValidColor hex-color) true)
    (-> (c/hexToRgb hex-color)
        (c/blend bg-color percent-opacity)
        (c/rgbArrayToHex)
        (js->clj))))

(defn generate-color-palette
  [primary-color]
  (let [palette {}]
    (assoc palette
           "50"  (split-color primary-color 0.05 (array 255 255 255))
           "100" (split-color primary-color 0.1 (array 255 255 255))
           "200" (split-color primary-color 0.2 (array 255 255 255))
           "300" (split-color primary-color 0.4 (array 255 255 255))
           "400" (split-color primary-color 0.6 (array 255 255 255))
           "500" (split-color primary-color 0.8 (array 255 255 255))
           "600" primary-color
           "700" (split-color primary-color 0.8 (array 0 0 0))
           "800" (split-color primary-color 0.6 (array 0 0 0))
           "900" (split-color primary-color 0.4 (array 0 0 0)))))

(defn- desaturate-color
  [hex-color n]
  (let [[h _ l] (c/hexToHsl hex-color)]
    (c/hslArrayToHex #js [h n l])))

(defn generate-grayscale-palette
  [hex-color]
  (let [themed-gray
        (-> hex-color
            (desaturate-color (/ 1 8))
            c/hexToRgb)]
    {"50" (c/rgbArrayToHex (c/lighten themed-gray 0.91))
     "100" (c/rgbArrayToHex (c/lighten themed-gray 0.79))
     "200" (c/rgbArrayToHex (c/lighten themed-gray 0.67))
     "300" (c/rgbArrayToHex (c/lighten themed-gray 0.55))
     "400" (c/rgbArrayToHex (c/lighten themed-gray 0.43))
     "500" (c/rgbArrayToHex (c/lighten themed-gray 0.31))
     "600" (c/rgbArrayToHex (c/lighten themed-gray 0.19))
     "700" (c/rgbArrayToHex (c/lighten themed-gray 0.07))
     "800" (c/rgbArrayToHex (c/darken themed-gray 0.05))
     "900" (c/rgbArrayToHex (c/darken themed-gray 0.17))}))

(defn hex->rgba [hex-color alpha]
  (let [[r g b] (c/hexToRgb hex-color)]
    (ca/rgbaArrayToRgbaStyle #js [r g b alpha])))

(defn get-index
  [coll key-name id]
  (first
   (keep-indexed
    (fn [idx item]
      (when (= (key-name item) id)
        idx))
    coll)))

;; LocalStorage Helpers

(defn set-item
  ([k v]
   (set-item k v {:json true}))
  ([k v {:keys [json]}]
   (try
     (.setItem js/localStorage k (if json
                                   (.stringify js/JSON (clj->js v))
                                   (clj->js v)))
     (catch js/Error _
       (re-frame/dispatch [:report-error {:name "LocalStorage Write Failure"
                                          :tab-name "LocalStorage"
                                          :payload {:local-storage-keys (j/call js/Object :keys js/localStorage)}}])))))

(defn get-item
  [k]
  (try
    (when-let [item (.getItem js/localStorage k)]
      (.parse js/JSON item))
    (catch js/Error _
      (re-frame/dispatch [:report-error {:name "LocalStorage Read Failure"
                                         :tab-name "LocalStorage"}]))))

(defn clean-url
  [url]
  (-> url
      js/decodeURIComponent
      (str/replace "\\" "")))

(defn truncate
  "Truncates a number n decimals places"
  [number decimals]
  (when (some? number)
    (js/Number (.toFixed number decimals))))

(defn calc-percent
  [total value]
  (* (/ value total) 100))

(defn calc-percent-floored
  [total value]
  (.floor js/Math (calc-percent total value)))

(defn calc-percent-rounded
  [total value]
  (.round js/Math (calc-percent total value)))

(defn calc-percent-change
  [start end]
  (if (zero? start)
    0
    (truncate (* (/ (- end start) start) 100) 2)))

(def ->percent #(->> % (* 100) (gstring/format "%.0f")))

(defn percent->passing-limit
  "Converts a percent to the minimum value (ceiling) integer needed
   get at least that percentage."
  [percent total]
  (js/Math.ceil (* percent total)))

(defn is-mobile?
  []
  (->> js/navigator
       (.-userAgent)
       (re-find #"Android|iPhone|webOS|iPad|iPod|BlackBerry|Windows Phone")
       (some?)))

(defn add-touch-event
  "Adds click events if on computer and touch on mobile devices"
  [options event]
  (if (is-mobile?)
    (assoc options :on-touch-end event)
    (assoc options :on-click event)))

(defn get-viewport-height
  "Returns the caclulated viewport height"
  []
  (.max js/Math
        (-> js/document .-documentElement .-clientHeight)
        (.-innerHeight js/window)
        0))

(defn get-thumbnail-url
  [{:keys [activity-uuid resource-uuid time]
    :or {time 0}}]
  (let [time (js/Math.floor time)]
    (str "/v1/activity/"
         activity-uuid
         "/videos/"
         resource-uuid
         "/thumbnails/"
         (if (zero? time) 1 time)
         "?size=large")))

(defn map-user-notes-to-moments
  "Attaches User notes to designed moments"
  [{:keys [user-notes moments]}]
  (let [moment-user-notes
        (->> user-notes
             (filter #(= (keyword (get-in % [:metadata :type])) :moment))
             (group-by #(get-in % [:metadata :value :uuid])))]
    (map #(assoc % :user-note (get-in moment-user-notes [(:uuid %) 0]))
         moments)))

(defn ->moment-type-title
  [moment-type]
  (case (keyword moment-type)
    :timeline-user-note (i18n/t* ::moment-title-timeline-user-note)
    :note (i18n/t* ::moment-title-note)
    :question (i18n/t* ::moment-title-question)
    :highlight (i18n/t* ::moment-title-highlight)
    :text (i18n/t* ::moment-title-text)

    (js/console.error
     (str "Moment type not supported. '" (keyword moment-type) "'"))))

(defn ->moment-type
  [moment]
  (if (some? (:question moment))
    :question
    :note))

(defn ->moment-type-str
  [moment]
  (-> moment ->moment-type name))

(defn ->moment-author [moment]
  (if (some? (:user-uuid moment))
    :learner
    :educator))

(defn ->moment-author-str
  [moment]
  (-> moment ->moment-author name))

(defn add-idx
  "Adds the index value to a collection of maps"
  [coll]
  (->> coll
       (map-indexed #(assoc %2 :idx %1))
       (vec)))

(defn- calc-percent-raw
  [total value]
  (* (/ value total) 100))

(defn- calc-video-progress
  [{:keys [progress duration]}]
  (if (and progress duration)
    (calc-percent-raw duration progress)
    0))

(defn get-video-progress
  [player-attributes]
  (let [{:keys [time duration]} @player-attributes]
    (if (js/Number.isNaN duration)
      0
      (calc-video-progress {:progress time
                            :duration duration}))))

(defn ie? []
  (->> (j/get js/navigator .-userAgent)
       (re-find #"MSIE|Trident")
       (some?)))

(defn scroll-to
  [element {:keys [top left]
            :or {top 0 left 0}}]
  (if (ie?)
    (if (element? element)
      (-> element
          (j/assoc! .-scrollTop top)
          (j/assoc! .-scrollLeft left))
      (j/call element .-scroll left top))
    (j/call element .-scrollTo #js {:top top
                                    :left left
                                    :behavior "smooth"})))

(defn last-touched
  "Returns the date the object was last touched"
  [{:keys [created-at updated-at]}]
  (or updated-at created-at))

(defn model->cms-time
  [model]
  (let [{:keys [created-at updated-at]} model]
    (if (some? (or created-at updated-at))
      (->> model
           last-touched
           coerce/from-string
           (js/Date.)
           (i18n/t* ::model->cms-time-format)
           (i18n/t* ::model->cms-time-format-last-updated))
      (i18n/t* ::model->cms-time-format-no-date-time))))

(defn is-image?
  [file]
  (let [image-file-extensions #{:png :jpg :jpeg :gif}]
    (contains? image-file-extensions
               (-> file
                   (str/split ".")
                   (last)
                   (keyword)))))

(defn file-path->file-name
  [file-path]
  (-> file-path
      (str/split "/")
      (last)
      (js/decodeURI)))

(defn coll->dictionary
  ([coll]
   (coll->dictionary coll :uuid))
  ([coll key-name]
   (into {} (map (juxt key-name identity) coll))))

(defn kebab-case
  "Turns a string into kebab-case.Ex 'Hello World 123' -> 'hello-world-123'"
  [input-string]
  (if (string? input-string)
    (-> input-string
        .toLowerCase
        (str/replace #" " "-")
        (str/replace #"[^a-z0-9\-]" ""))
    (js/console.error "kyber.utils/kebab-case Invalid input")))

(defn video-activity?
  [activity]
  (= (or (:activity/plo-type activity)
         (:plo-type activity))
     "video"))
(defn pdf-activity?
  [activity]
  (= (or (:activity/plo-type activity)
         (:plo-type activity))
     "pdf"))
(defn plo->label [plo-type]
  (get {"pdf" "document"
        "video" "video"}
       plo-type))
(defn pdf-moment? [{:keys [xfdf]}] (some? xfdf))
(defn video-moment? [moment] (not (pdf-moment? moment)))

(defn highlighted-moment-resource
  [{:keys [highlighted-resource resources]}]
  (->> resources (filter #(= (:uuid %) highlighted-resource)) first))

(defn local-storage-merge
  "Merges an object into localStorage"
  [object]
  (let [local-storage-map (js->clj object :keywordize-keys true)]
    (when (map? local-storage-map)
      (doseq [[k v] local-storage-map]
        (js-invoke js/localStorage "setItem" (name k) v)))))

(defn local-storage-dump
  "Copies the localStorage to clipboard"
  []
  (let [el (js-invoke js/document "createElement" "textarea")
        localStorage (gobj/getValueByKeys js/window "localStorage")
        creds (js-invoke js/JSON "stringify" localStorage)]
    (gobj/set el "value" creds)
    (gobj/set el "readonly" "")
    (gobj/set el "style" (clj->js {:position :absolute
                                   :left "-9999px"}))
    (js-invoke (gobj/getValueByKeys js/document "body")
               "appendChild" el)
    (js-invoke el "select")
    (js-invoke js/document "execCommand" "copy")
    (js-invoke (gobj/getValueByKeys js/document "body")
               "removeChild" el)
    "Copied to clipboard"))

;; Sets local-storage-merge to the window for development access
;;   Usecase: Takes the result of copy(JSON.stringify(localStorage)) from
;;            another session to load it for local development.
(when ^boolean js/goog.DEBUG
  (gobj/set js/window "localStorageMerge" local-storage-merge))

(when ^boolean js/goog.DEBUG
  (gobj/set js/window "localStorageDump" local-storage-dump))

(defn sanitize-string [str]
  (if-not (string? str)
    (throw (js/Error. "Expected a string"))
    (str/replace str (re-pattern "[|\\\\{}()[\\]^$+*?.]") "\\$&")))

(defn sanitize-rich-text [rich-text]
  (-> rich-text
      (str/replace #"<[^>]+>" "")
      (str/replace #"&nbsp;" "")))

(defn get-next-tab-url
  [tab-key]
  (let [{:keys [path] :as current-url}
        (-> js/window .-location url)]
    (str path "?" (-> current-url
                      (assoc-in [:query "tab"] (name tab-key))
                      .-query
                      map->query))))

;; TODO: Move into a common ns that's usable by both server and clients.
;; common/specs exists, but seems that should just be for specs and not utils.
;;
;; Server util here: https://github.com/Archemedx/kyber/blob/develop/server/src/common/map_utils.cljs
(defn kw->qkw
  "Converts a simple keyword to a qualified keyword"
  [ns kw]
  (if (and (keyword? kw) (not (qualified-keyword? kw)))
    (keyword (if (keyword? ns)
               (name ns)
               (str ns))
             (name kw))
    kw))

(defn map->nsmap
  "Performs a shallow transform of the simple keys to qualified keywords"
  [ns map]
  (reduce-kv
   (fn [acc k v] (assoc acc (kw->qkw ns k) v))
   {}
   map))

(defn qkw->kw
  "Converts a qualified keyword to a simple keyword"
  [kw]
  (if (qualified-keyword? kw)
    (-> kw name keyword)
    kw))

(defn nsmap->map
  "Performs a shallow transform of the qualified keys to simple keywords"
  [map]
  (reduce-kv
   (fn [acc k v] (assoc acc (qkw->kw k) v))
   {}
   map))

(defn set-primary-learning-object
  [{:keys [video
           plo-type
           pdf-resource-uuid
           primary-learning-object]
    :as activity}]
  (if (nil? (:resource/uuid primary-learning-object))
    (assoc-in activity [:primary-learning-object :resource/uuid]
              (condp = plo-type
                "pdf" pdf-resource-uuid
                "video" video
                nil))
    activity))

(defn select-ns-keys [m namespace-value]
  (reduce
   (fn [acc [k v]]
     (if (= (namespace k) (name namespace-value))
       (assoc acc k v)
       acc))
   {}
   m))

(defn idx->letters [num]
  (loop [loop-num num combined-letters ""]
    (let [letter (str (char (+ (mod loop-num 26) 65)))
          quotient (quot loop-num 26)]
      (if (> quotient 0)
        (recur (dec quotient) (str letter combined-letters))
        (str letter combined-letters)))))
